import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Download, Trash, ShieldCheck, WarningOctagon, ShareNetwork, Copy } from "@phosphor-icons/react";
import RegionSelect from "@/components/RegionSelect";
import { fetchRegions, listShares, createShare, deleteShare } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [regions, setRegions] = useState([]);
  const [shares, setShares] = useState([]);
  const [shareRegion, setShareRegion] = useState("");

  useEffect(() => {
    (async () => {
      const [rs, sh] = await Promise.all([fetchRegions(), listShares()]);
      setRegions(rs);
      setShares(sh);
      if (rs[0]) setShareRegion(rs[0].id);
    })();
  }, []);

  const refreshShares = async () => setShares(await listShares());

  const onCreateShare = async () => {
    if (!shareRegion) return;
    const r = await createShare(shareRegion);
    if (r.ok) {
      toast.success("Share-Link erstellt");
      refreshShares();
    } else toast.error("Konnte Link nicht erstellen");
  };

  const onRevokeShare = async (id) => {
    await deleteShare(id);
    toast.success("Link widerrufen");
    refreshShares();
  };

  const onCopy = (token) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link kopiert");
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/me/export`, { credentials: "include" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bodenblick-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Datenexport heruntergeladen");
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  const onDelete = async () => {
    if (confirmText !== "LÖSCHEN") {
      toast.error("Bitte gib LÖSCHEN (Großbuchstaben) ein");
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/me`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      toast.success("Dein Konto wurde gelöscht. Auf Wiedersehen.");
      setTimeout(() => { logout(); }, 1200);
    } catch (e) {
      toast.error("Löschung fehlgeschlagen — bitte schreib an vision@bodenblick.com");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-root">
      <header>
        <div className="eid-label">Konto · Datenschutz</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Einstellungen</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Dein DSGVO-Selbstbedienungs-Bereich. Angemeldet als{" "}
          <strong className="text-white">{user?.email}</strong>.
        </p>
      </header>

      <section className="eid-card p-5 space-y-3" data-testid="settings-export-card"
               style={{ borderLeft: "3px solid #00FA9A" }}>
        <div className="flex items-start gap-3">
          <span className="p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A] shrink-0">
            <ShieldCheck size={22} />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-medium tracking-tight">Daten exportieren (Art. 15 DSGVO)</h2>
            <p className="text-sm text-zinc-400 mt-1">
              Lädt alle zu deinem Konto gespeicherten Daten als JSON-Datei herunter:
              Stammdaten, Sessions und verknüpfte Newsletter-Einträge.
            </p>
            <button
              onClick={onExport}
              disabled={exporting}
              data-testid="export-data-btn"
              className="mt-3 inline-flex items-center gap-2 bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 rounded-full px-4 py-2 text-sm font-medium"
            >
              <Download size={16} />
              {exporting ? "Export läuft…" : "Meine Daten herunterladen"}
            </button>
          </div>
        </div>
      </section>

      <section className="eid-card p-5 space-y-3" data-testid="settings-share-card"
               style={{ borderLeft: "3px solid #497B94" }}>
        <div className="flex items-start gap-3">
          <span className="p-2 rounded-sm bg-[#D6E4EC] text-[#7CC4E8] shrink-0">
            <ShareNetwork size={22} />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-medium tracking-tight">Öffentliche Region-Links</h2>
            <p className="text-sm text-zinc-400 mt-1">
              Erzeuge einen anonymisierten Read-Only-Link für eine Region. Andere
              können ihn öffnen — sehen aber nur Score, NDVI, Bodenfeuchte und
              Ertrags-Index, keine GPS-Daten. Jederzeit widerrufbar.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <RegionSelect regions={regions} value={shareRegion} onChange={setShareRegion} testid="share-region-select" />
              <button
                onClick={onCreateShare}
                data-testid="share-create-btn"
                className="inline-flex items-center gap-1.5 bg-[#1C4257] text-white hover:bg-[#0E2230] rounded-full px-4 py-2 text-sm"
              >
                Link erstellen
              </button>
            </div>

            {shares.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="shares-list">
                {shares.map((s) => {
                  const region = regions.find((r) => r.id === s.region_id);
                  const url = `${window.location.origin}/share/${s.token}`;
                  return (
                    <div key={s.id} className="flex items-center gap-2 border border-white/10 rounded-sm px-3 py-2"
                         data-testid={`share-item-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{region?.label || s.region_id}</div>
                        <div className="text-[11px] font-mono text-zinc-500 truncate">{url}</div>
                      </div>
                      <button
                        onClick={() => onCopy(s.token)}
                        className="p-1.5 text-zinc-400 hover:text-white"
                        aria-label="Kopieren" data-testid={`share-copy-${s.id}`}
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        onClick={() => onRevokeShare(s.id)}
                        className="p-1.5 text-zinc-400 hover:text-[#FF7A45]"
                        aria-label="Widerrufen" data-testid={`share-revoke-${s.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="eid-card p-5 space-y-3" data-testid="settings-delete-card"
               style={{ borderLeft: "3px solid #FF5500" }}>
        <div className="flex items-start gap-3">
          <span className="p-2 rounded-sm bg-[#FF5500]/10 text-[#FF7A45] shrink-0">
            <WarningOctagon size={22} />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-medium tracking-tight">Konto löschen (Art. 17 DSGVO)</h2>
            <p className="text-sm text-zinc-400 mt-1">
              Unwiderruflich. Dein Konto, alle Sessions und verknüpften
              Newsletter-Einträge werden sofort gelöscht. Die Aktion kann
              nicht rückgängig gemacht werden.
            </p>
            <div className="mt-3 space-y-2">
              <label className="block text-xs text-zinc-400">
                Tippe zur Bestätigung „LÖSCHEN" (Großbuchstaben):
              </label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="delete-confirm-input"
                className="w-full md:w-64 border border-white/10 bg-[#121212] rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#FF5500]/40"
                placeholder="LÖSCHEN"
              />
              <button
                onClick={onDelete}
                disabled={deleting || confirmText !== "LÖSCHEN"}
                data-testid="delete-account-btn"
                className="inline-flex items-center gap-2 bg-[#FF5500] text-white hover:bg-[#8C3B25] disabled:opacity-40 rounded-full px-4 py-2 text-sm font-medium"
              >
                <Trash size={16} />
                {deleting ? "Lösche…" : "Konto endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="eid-card p-5 space-y-2" data-testid="settings-contact-card">
        <div className="eid-label">Alternativer Weg</div>
        <p className="text-sm text-zinc-400">
          Für alle weiteren DSGVO-Anliegen (Berichtigung, Einschränkung,
          Widerspruch): formlose E-Mail an{" "}
          <a href="mailto:vision@bodenblick.com" className="underline text-white">
            vision@bodenblick.com
          </a>
          . Wir antworten innerhalb eines Monats.
        </p>
      </section>
    </div>
  );
}
